import { inject, Injectable, signal } from '@angular/core';
import { SchedulingModalityEnum } from '@core/enums';
import { IDefaultService, IDefaultServiceOption } from '@core/interface';
import { SERVICE_TYPE_ID } from '@core/utils';
import { VehicleService } from '../vehicle/vehicle.service';
import { GetCountryService } from '../country/country.service';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyOSRPipe } from '@core/pipes';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class ServicesFormatterService {
  readonly #userService = inject(UserService);
  readonly #vehicleService = inject(VehicleService);
  readonly #countryService = inject(GetCountryService);
  readonly #translateService = inject(TranslateService);
  readonly #currencyFormatter = inject(CurrencyOSRPipe);

  country = signal<string>(this.#countryService.getCountry());

  /**
   * Faz todas as filtragens necessárias para exibir apenas os serviços opcionais
   * @param services Lista do tipo IDefaultService
   * @param modality Modalidade de agendamento
   * @returns A lista de serviços filtrados
   */
  // TODO: Essa filtragem poderia ser feita de forma mais específica pelo backend?
  filterOptionalServices(
    services: IDefaultService[],
    modality: string
  ): IDefaultService[] {
    const originalServices = services;
    const isMSV = modality === SchedulingModalityEnum.MSV;

    // Removeremos da lista de serviços:
    services = services.filter(
      (service: IDefaultService) =>
        // Os que são revisões:
        !service.prevMaintenance &&
        // Os que estão inativos:
        service.active &&
        // Os que não devem ser exibidos para o cliente:
        service.displayToCustomers &&
        // Os que são compatíveis com MSV (se for o caso):
        (!isMSV || service.msvApplicable) &&
        // Os que são de modalidades (P&D e MSV):
        !service.serviceType.pickupDelivery &&
        !service.serviceType.mobileService &&
        // Os que são de lavagem:
        !service.serviceName.toLocaleLowerCase().includes('lavagem')
    );

    // Segundo o componente não otimizado, Brasil e Argentina parecem ter regras específicas de exibição de
    // serviços. Então, aplicamos esses filtros adicionais usando "número mágicos" (o que não é recomendado).
    // TODO: Sugerir filtragem pelo BE, já que ele já sabe o país do usuário pelo endpoint que chamamos.
    // Ou talvez limpar esses dados? Sem essas regras alguns serviços com prefixo "NEW" retornam, e ninguém com
    // quem conversei sabe dizer qual a origem e pra qual caso são usados.
    if (this.country() === 'BRA') {
      services = services.filter((service) => {
        return (
          (service.serviceTypeId == SERVICE_TYPE_ID.BRA.alignmentAndBalancing ||
            service.serviceTypeId == SERVICE_TYPE_ID.BRA.diagnosis ||
            service.serviceTypeId == SERVICE_TYPE_ID.BRA.repairs ||
            service.serviceTypeId == SERVICE_TYPE_ID.BRA.services) &&
          service.serviceId != 32 &&
          service.serviceId != 968
        );
      });
    } else if (this.country() === 'ARG') {
      services = services.filter((service) => {
        return (
          (service.serviceTypeId == SERVICE_TYPE_ID.ARG.alignmentAndBalancing ||
            service.serviceTypeId == SERVICE_TYPE_ID.ARG.bodyworkAndPainting ||
            service.serviceTypeId == SERVICE_TYPE_ID.ARG.contactless ||
            service.serviceTypeId == SERVICE_TYPE_ID.ARG.diagnosis ||
            service.serviceTypeId == SERVICE_TYPE_ID.ARG.mobileService ||
            (service.serviceTypeId == SERVICE_TYPE_ID.ARG.service &&
              !service.prevMaintenance)) &&
          service.serviceId != 32 &&
          service.serviceId != 968
        );
      });
    } else {
      services = services.filter(
        (service) =>
          service &&
          !service.prevMaintenance &&
          !service.serviceName.toUpperCase().includes('RECALL')
      );
    }

    // Como última filtragem, montamos serviços específicos para o modelo do veículo
    // se algum dos serviços possuir essa configuração.
    services = this.filterServicesByVehicleModel(services);

    // Ordenamos alfabeticamente
    services = services.sort((a, b) =>
      a.serviceName.localeCompare(b.serviceName)
    );

    // Os serviços de ID 32 e 968 são os de "Outros Serviços (Detalhar no campo OBS)",
    // por isso são sempre adicionados no fim do array, se estiverem configurados com as flags corretas.
    const obsServices = [32, 968];
    obsServices.forEach((id) => {
      const exists = services.some((service) => service.serviceId === id);
      if (!exists) {
        const original = originalServices.find(
          (service) => service.serviceId === id
        );
        if (original) {
          services.push(original);
        }
      }
    });
    return services;
  }

  /**
   * Lógica de filtragem de serviços por modelo de veículo
   * @param services Lista do tipo IDefaultService
   * @returns A lista de serviços filtrados
   */
  // TODO: Não seria possível enviarmos o modelo para o BE e ele já retornar os serviços filtrados?
  private filterServicesByVehicleModel(
    services: IDefaultService[]
  ): IDefaultService[] {
    if (
      this.#vehicleService.vehicleComputed() ||
      !this.#vehicleService.vehicleComputed()?.modelGroup
    ) {
      return services;
    }

    return services.map((service) => {
      if (
        !service.servicesByModelGroup ||
        service.servicesByModelGroup.length === 0
      ) {
        return service;
      }

      const matchingModelService = service.servicesByModelGroup.find(
        (serviceByModel) => {
          const modelId = serviceByModel.model?.id;
          const vehicleModelGroup =
            this.#vehicleService.vehicleComputed()?.modelGroup;

          return (
            modelId !== null &&
            modelId !== undefined &&
            vehicleModelGroup !== null &&
            vehicleModelGroup !== undefined &&
            (modelId.toString() === vehicleModelGroup.toString() ||
              Number(modelId) === Number(vehicleModelGroup))
          );
        }
      );

      // Construímos o objeto de serviço atualizado com os dados específicos para aquele modelo
      if (matchingModelService) {
        return {
          ...service,
          servicePrice: matchingModelService.servicePrice,
          duration: matchingModelService.duration,
          fixedPrice: matchingModelService.priceFixed,
          promotionalActive: matchingModelService.promotionalActive,
          promotionalPrice: matchingModelService.promotionalPrice,
          promotionalDescription: matchingModelService.promotionalDescription,
          promotionalStartDate: matchingModelService.promotionalStartDate,
          promotionalEndDate: matchingModelService.promotionalEndDate,
          promotionalStartHour: matchingModelService.promotionalStartHour,
          promotionalEndHour: matchingModelService.promotionalEndHour,
          dmsCode: matchingModelService.dmsCode,
        };
      }

      return service;
    });
  }

  /**
   * Lógica de filtragem de serviços por modelo de veículo
   * @param services Lista do tipo IDefaultService
   * @returns A lista de serviços filtrados
   */
  formatServicesAsItems(
    services: IDefaultService[]
  ): IDefaultServiceOption[] {
    return services.map((service) => {
      return {
        id: service.serviceId,
        description: service.name,
        serviceName: service.serviceName,
        price: service.servicePrice,
        formattedPrice: this.formatServicePrice(service),
        isCostless: service.isCostless || service.costless,
        isSelected: false,
        isNeedComment: service.displayComment,
      };
    });
  }

  /**
   * Lógica de formatação de preço obedecendo regras de negócio do OSR
   * @param service Serviço que terá o preço formatado
   * @returns O preço formatado do serviço
   */
  private formatServicePrice(service: IDefaultService): string {
    // Se ele estiver como costless, é gratuito, não importando o resto.
    if (service.isCostless || service.costless) {
      return this.#translateService.instant('schedule.no-cost');
    }

    // Se ele tiver preço e não for costless, formatamos o preço normalmente.
    const lang = this.#userService.userComputed()?.languages?.preferredLanguage as string;
    if (service.servicePrice && service.servicePrice !== 0 && lang) {
      return this.#currencyFormatter.transform(service.servicePrice, lang);
    }

    // Se não for costless mas mesmo assim não tiver preço, então é sob consulta.
    // Também usamos esse caso de fallback caso ocorra um problema em alguma outra lógica.
    return this.#translateService.instant('schedule.price-on-request');
  };
}
